<script setup>
import { useDepositStore } from "@stores/deposit";
import { useSettingsStore } from "@stores/settings";
import { useCouponStore } from "@stores/coupon";
import { useAuthStore } from "@stores/auth";
import useDeposit from "@composables/base/useDeposit";

const settingsStore = useSettingsStore();
const depositStore = useDepositStore();
const couponStore = useCouponStore();
const authStore = useAuthStore();

const minimumAmountDeposit = ref(`R$ ${(settingsStore.settings.deposit_min / 100).toFixed(2)}`);
const pix = ref("PIX");

const {
  image,
  loading,
  modalBanner,
  depositSection,
  isCountdownActive,
  depositId,
  showIcon,
  depositData,
  inputAmount,
  formError,
  newDeposit,
  fetchPayment,
  post,
  formatInput,
  setDepositAmount,
  copyPixCode,
  transformSlotProps,
  handleInputAmount
} = useDeposit();

const closeDepositModal = () => {
  depositStore.toggleModal();
  newDeposit();
};

const handlePost = async (e) => {
  if (e) e.preventDefault();

  try {
    if (!authStore.user?.document?.number) {
      authStore.toggleUserDocumentModal();
      return;
    }
    
    return post();
  } catch (error) {
    console.error('Ocorreu um erro durante o depósito:', error);
    depositStore.toggleModal(false);
  }
};
</script>

<template>
  <BaseModal :show="depositStore.isModalOpen" :modal-banner-url="modalBanner ? modalBanner : null"
             @close="closeDepositModal">
    <!-- Banner -->

    <section v-if="depositSection === 0"
             class="scrollbar-hide relative flex h-full select-none flex-col bg-brand-card xl:rounded-md">
      <div class="absolute right-2 top-2 cursor-pointer"></div>

      <form @submit="handlePost">
        <section class="w-full text-white sm:mt-0">
          <!-- Deposit Form Section -->
          <div v-if="depositSection === 0" class="flex justify-center">
            <div class="bg-background2 w-full" v-show="!image">
              <div class="flex flex-col">
                <h2 class="text-lg font-bold">Depositar</h2>
                <p class="mb-2 text-sm font-semibold my-1">Adicione saldo à sua conta.</p>

                <!--                <DepositGateway class="my-4" />-->

                <div class="flex gap-2">
                  <div class="relative w-1/2">
                    <BaseInput
                      type="text"
                      placeholder="Método"
                      disabled
                      class="w-full mb-2"
                      readonly
                      v-model="pix"
                    />
                    <img
                      src="/public/imgs/pix.svg"
                      alt="PIX icon"
                      class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-25"
                    />
                  </div>
                  <BaseInput
                    type="text"
                    placeholder="Valor mínimo"
                    v-model="minimumAmountDeposit"
                    disabled
                    class="w-1/2 mb-2 text-xs"
                    readonly
                  />
                </div>
                <div class="relative my-2">
                  <client-only>
                    <BaseInput @input="handleInputAmount" currency type="text" name="amount" v-model="inputAmount"
                               class="border-red-900 bg-red-600" />
                  </client-only>
                  <div class="absolute opacity-40 right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <img
                      src="/public/imgs/flags/brazil-flag-icon.svg"
                      alt="Brazilian flag"
                      class="w-5 h-4 rounded-full"
                    />
                    <span class="text-sm font-medium">BRL</span>
                  </div>
                </div>

                <div v-if="settingsStore.settings.deposit_popup_values" class="mt-4 mb-3">
                  <div class="grid grid-cols-3 w-full gap-3">
                    <div
                      v-for="(value, index) in settingsStore.settings.deposit_popup_values.split(',')"
                      :key="value"
                      class="relative flex justify-center hover:brightness-125 transition-all"
                    >
                      <div
                        v-if="index % 2 === 1"
                        class="absolute top-0 right-0 bg-warning text-black text-[10px] font-bold px-2 rounded-tr-lg rounded-bl-lg transform translate-x-1/1 -translate-y-1/1 shadow-md"
                      >
                        HOT
                      </div>
                      <button
                        @click.prevent="setDepositAmount(value)"
                        class="flex items-center justify-center text-brand-primary font-medium text-sm sm:text-base border w-full h-12 md:h-14 rounded-lg border-brand-primary bg-brand-primary/15"
                      >
                        {{ money(value).split(",")[0] }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <DepositCoupon class="mt-4" type="deposit" />

              <BaseComplementAlert type="error" :message="formError" />

              <BaseButton
                :disabled="loading"
                @click.prevent="handlePost"
                class="mt-8 h-12 w-full rounded bg-brand-primary py-2 text-white hover:bg-brighten-brand-primary duration-150"
                :class="{ 'opacity-50': formatInput === '0,00' }"
              >
                <span v-if="!loading && !couponStore.isCouponLoading">Depositar</span>
                <span v-if="couponStore.isCouponLoading" class="inline-flex items-center gap-2">
                <span>Aplicando cupom</span>
                <Icon name="fa6-solid:circle-notch" class="animate-spin" />
              </span>
                <span v-if="loading">
                Carregando
                <Icon class="animate-spin" name="fa6-solid:circle-notch" />
              </span>
              </BaseButton>

              <div class="flex items-center justify-between mt-2 px-3" v-if="settingsStore.settings.general_payment_gateway_name">
                <small class="text-xs opacity-60">Processado por</small>
                <small class="text-xs flex items-center gap-1">
                  <img
                    src="/public/imgs/pix.svg"
                    alt="PIX icon"
                    class="w-3 h-3 rounded-full"
                  />
                  <span>{{ settingsStore.settings.general_payment_gateway_name }}</span>
                </small>
              </div>
            </div>
          </div>
        
          <!-- Loading State -->
          <div
            v-if="depositSection === 1 && !depositData.payment_image"
            class="flex h-full items-center justify-center"
          >
            <Icon class="animate-spin text-4xl" name="svg-spinners:180-ring-with-bg" />
          </div>
        </section>
      </form>
    </section>

    <!-- QR Code Section -->
    <section v-else-if="depositSection === 1" class="scrollbar-hide w-full text-white">
      <div v-if="depositData.payment_image" class="bg-background2 mt-3">
        <!-- Countdown Timer -->
        <vue-countdown v-if="isCountdownActive" class="mb-4 flex w-full rounded-sm bg-red-500 p-2"
                       :time="settingsStore.settings.deposit_popup_timer * 60 * 1000"
                       v-slot="{ days, hours, minutes, seconds }"
                       :transform="transformSlotProps">
          <div class="flex w-full items-center justify-center gap-3 text-center">
            <span class="text-xs">Faça o pagamento em até:</span>
            <div class="text-lg font-bold">
              <span>{{ minutes }}</span> : <span>{{ seconds }}</span>
            </div>
          </div>
        </vue-countdown>

        <!-- Status Indicators -->
        <p v-if="depositData.status === 0" class="rounded-lg bg-yellow-400 p-1 text-center font-semibold text-black">
          Pendente
        </p>
        <p v-else-if="depositData.status === 1" class="rounded-lg bg-red-400 p-1 text-center font-semibold text-black">
          Negado
        </p>
        <p v-else-if="depositData.status === 2"
           class="rounded-lg bg-green-400 p-1 text-center font-semibold text-black">
          Aprovado
        </p>

        <!-- QR Code Image -->
        <div class="flex flex-col items-center justify-center">
          <img v-if="depositData.payment_image" :src="'data:image/png;base64,' + depositData.payment_image" alt="pix"
               class="mt-4 w-72" />
        </div>

        <!-- Deposit Amount -->
        <div class="mt-3 flex justify-center font-semibold">
          <p>Valor de depósito: {{ money(depositData.amount / 100) }}</p>
        </div>

        <!-- PIX Code -->
        <div class="py-3">
          <p class="px-2 text-center text-xs" style="word-break: break-all" ref="mylink">
            {{ depositData.payment_code }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-center gap-2 py-3">
          <button @click="copyPixCode" class="flex items-center space-x-1 rounded bg-blue-500 px-3 py-1 text-white">
            <span>Copiar</span>
            <Icon v-if="showIcon" name="clarity:success-standard-line" class="h-5 w-5" />
          </button>

          <button @click="newDeposit()"
                  class="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 active:bg-green-700">
            Novo
          </button>
        </div>

        <div class="flex flex-col items-center py-3">
          <p class="px-2 text-center text-xs">
            Obs: Caso tenha efetuado o pagamento, atualize a página para
            visualizar o status atualizado.
          </p>
          <button @click="fetchPayment(depositId)" class="mt-4 rounded bg-blue-500 p-1 px-2 text-sm text-white">
            <span>
              Atualizar
              <Icon v-if="loading" class="animate-spin" name="fa6-solid:circle-notch" />
            </span>
          </button>
        </div>
        <div class="flex items-center justify-between mt-4 px-3 w-full" v-if="settingsStore.settings.general_payment_gateway_name">
          <small class="text-xs opacity-60">Processado por</small>
          <small class="text-xs flex items-center gap-1">
            <img
              src="/public/imgs/pix.svg"
              alt="PIX icon"
              class="w-3 h-3 rounded-full"
            />
            <span>{{ settingsStore.settings.general_payment_gateway_name }}</span>
          </small>
        </div>

      </div>
    </section>

    <!-- Loading State -->
    <div v-if="depositSection === 1 && !depositData.payment_image" class="flex h-full items-center justify-center">
      <Icon class="animate-spin text-4xl" name="svg-spinners:180-ring-with-bg" />
    </div>
  </BaseModal>
  <DepositUserDocument v-if="depositStore.isModalOpen" @completed="post()" />
</template>